import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { Typography } from '../../../visual-components/typography/typography';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { SearchParam } from '../../../core/routing/param';
import { Divider } from '../../../visual-components/divider/divider';
import { LoadingSpinner } from '../../../visual-components/loading-spinner/loading-spinner';
import isNil from '../../../core/helpers/is-nil';
import { useFindArticlesQuery } from '../../../modules/article/queries';
import { useSearchParam } from '../../../core/routing/hooks/use-search-param';
import useDebounce from '../../../core/hooks/use-debounce';
import { InlineNotification } from '../../../visual-components/inline-notification/inline-notification';
import { track } from '../../../modules/analytics/track';
import { ALL_SEARCH_PARAMS, FilterSection } from './filter-section';
import { ResultSection } from './result-section';
import styles from './search-section.module.scss';

const ITEMS_PER_PAGE = 20;
const QUERY_DEBOUNCE_MS = 250;

export const SearchSection: React.FC = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const params = useSearchParam(ALL_SEARCH_PARAMS);

  const {
    query: queryParam,
    glosses,
    surfaces,
    categories,
    colorRanges,
    weatherResistances,
    effects,
    seriesNumbers,
    specialProperties,
    certificates,
  } = params;

  const [query, setQuery] = useState(queryParam);

  const page = params.page ?? 1;

  const debouncedQuery = useDebounce(query ?? '', QUERY_DEBOUNCE_MS);

  useEffect(
    function trackSearch() {
      if (debouncedQuery === '') {
        return;
      }
      track({
        type: 'article-search',
        data: {
          query: debouncedQuery,
        },
      });
    },
    [debouncedQuery],
  );

  const {
    data: articles,
    isLoading,
    error,
  } = useFindArticlesQuery({
    query: debouncedQuery,
    offset: page * ITEMS_PER_PAGE - ITEMS_PER_PAGE,
    limit: ITEMS_PER_PAGE,
    glosses,
    surfaces,
    categories,
    colorRanges,
    weatherResistances,
    effects,
    seriesNumbers,
    specialProperties,
    certificates,
  });

  return (
    <>
      <GridContainer>
        <GridItem span="9">
          <Typography variant="headline-2">{t('home.quickSearch')}</Typography>
          <Spacer size="06" sizeXs="05" />
        </GridItem>
        <FilterSection
          query={query ?? ''}
          setQuery={(e) => {
            setQuery(e.target.value);
            const clonedSearchParams = new URLSearchParams(searchParams);
            if (e.target.value === '') {
              clonedSearchParams.delete(SearchParam.query);
            } else {
              clonedSearchParams.set(SearchParam.query, e.target.value);
            }
            clonedSearchParams.delete(SearchParam.page);
            setSearchParams(clonedSearchParams);
          }}
          values={{
            ...params,
          }}
        />
        <GridItem span="12">
          <Divider />
        </GridItem>
      </GridContainer>
      {isLoading ? (
        <GridContainer>
          <GridItem span="12">
            <div className={styles.loadingSpinner}>
              <LoadingSpinner />
            </div>
          </GridItem>
        </GridContainer>
      ) : isNil(articles) ? (
        <GridContainer>
          <GridItem>
            <InlineNotification
              text={t('errorPage.tryLaterOrContactSupport')}
              title={t('errorPage.defaultPageTitle')}
              type="error"
            />
          </GridItem>
        </GridContainer>
      ) : (
        <ResultSection
          articles={articles}
          error={error}
          page={page}
          resetFilters={() => {
            const clonedSearchParams = new URLSearchParams(searchParams);
            ALL_SEARCH_PARAMS.forEach((param) => {
              clonedSearchParams.delete(param);
            });
            setSearchParams(clonedSearchParams);
            setQuery('');
          }}
          setPage={(page) => {
            const clonedSearchParams = new URLSearchParams(searchParams);
            clonedSearchParams.set(SearchParam.page, page.toString());
            setSearchParams(clonedSearchParams);
          }}
        />
      )}
      <Spacer size="07" />
    </>
  );
};
